/**
 * Service Archive 用スクリプト
 *
 * - text-invert: .js-text-invert 要素を SplitText で行ごとに分割し、
 *   ScrollTrigger で backgroundPositionX を 100% → 0% に scrub アニメ
 * - word-anim: .js-word-anim 要素を SplitText で単語ごとに分割し、
 *   ロード時に下からフェードイン
 * - parallax: .js-parallax-view 要素の子要素を data-speed に応じて
 *   スクロール連動で translateY 移動
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
// SplitText は GSAP 3.13+ で無償化(オープンソースライセンス)。
// package.json で gsap ^3.14.2 を使用中のため Club GSAP の認証は不要。
import { SplitText } from 'gsap/SplitText';
import { initRevealLines } from './animation/scroll-animations.js';
import { whenFontsReady } from './util/fonts.js';

function initHeroAnim() {
  const items = [
    document.querySelector('.p-service-archive__hero-title-sub'),
    document.querySelector('.p-service-archive__hero-title-main'),
    document.querySelector('.p-service-archive__hero-media'),
  ].filter(Boolean);

  if (!items.length) return;

  gsap.set(items, { opacity: 0, y: 24 });
  whenFontsReady(() => {
    gsap.to(items, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      stagger: 0.12,
      delay: 0.2,
    });
  });
}

gsap.registerPlugin(ScrollTrigger, SplitText);

function initTextInvert() {
  const targets = document.querySelectorAll('.js-text-invert');
  if (!targets.length) return;

  // Web フォント読込前に分割すると行幅がフォールバックフォント基準でずれるため待機
  whenFontsReady(() => {
    let split;
    try {
      split = new SplitText(targets, { type: 'lines' });
    } catch (err) {
      console.warn('[service] SplitText (text-invert) failed:', err);
      return; // SplitText 失敗時は要素を元のままにして終了
    }
    split.lines.forEach(line => {
      gsap.to(line, {
        backgroundPositionX: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: line,
          scrub: 0.3,
          start: 'top 90%',
          end: 'top 55%',
        },
      });
    });
  });
}

function initFadeAnim() {
  const items = document.querySelectorAll('.js-fade-anim');
  if (!items.length) return;

  items.forEach(item => {
    const direction = item.dataset.direction || 'bottom';
    const rawOffset = parseFloat(item.dataset.offset);
    const offset = Number.isFinite(rawOffset) ? rawOffset : 50;
    const rawDuration = parseFloat(item.dataset.duration);
    const duration = Number.isFinite(rawDuration) ? rawDuration : 1.15;
    const rawDelay = parseFloat(item.dataset.delay);
    const delay = Number.isFinite(rawDelay) ? rawDelay : 0.15;

    const settings = {
      opacity: 0,
      ease: 'power2.out',
      duration,
      delay,
      scrollTrigger: {
        trigger: item,
        start: 'top 85%',
      },
    };
    if (direction === 'top') settings.y = -offset;
    else if (direction === 'left') settings.x = -offset;
    else if (direction === 'right') settings.x = offset;
    else settings.y = offset;

    gsap.from(item, settings);
  });
}

function initRrTitleAnim() {
  const items = document.querySelectorAll('.js-rr-title-anim');
  if (!items.length) return;

  whenFontsReady(() => {
    items.forEach(target => {
      let split;
      try {
        split = new SplitText(target, { type: 'lines' });
      } catch (err) {
        console.warn('[service] SplitText (rr-title-anim) failed:', err);
        return; // SplitText 失敗時はその要素を素通り
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: target,
          start: 'top 90%',
          end: 'bottom 60%',
          toggleActions: 'play none none reverse',
        },
      });
      // 行ごとに分割（fonts.ready 後・try/catch で保護）
      gsap.set(target, { perspective: 400 });
      tl.from(split.lines, {
        duration: 1,
        delay: 0.3,
        opacity: 0,
        rotationX: -80,
        force3D: true,
        transformOrigin: 'top center -50',
        stagger: 0.2,
      });
    });
  });
}

function initWordAnim() {
  const items = document.querySelectorAll('.js-word-anim');
  if (!items.length) return;

  whenFontsReady(() => {
    items.forEach(item => {
      let split;
      try {
        split = new SplitText(item, { type: 'words' });
      } catch (err) {
        console.warn('[service] SplitText (word-anim) failed:', err);
        return;
      }
      gsap.from(split.words, {
        duration: 0.75,
        delay: 0.1,
        autoAlpha: 0,
        x: 20,
        stagger: 0.04,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: item,
          start: 'top 85%',
          once: true,
        },
      });
    });
  });
}

function initParallax() {
  const items = document.querySelectorAll('.js-parallax-view img[data-speed]');
  if (!items.length) return;

  const reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reducedMotionMq.matches) {
    // reduced-motion ユーザーには視差を提供しない
    return;
  }

  items.forEach(target => {
    const speed = parseFloat(target.dataset.speed);
    const speedValue = Number.isFinite(speed) ? speed : 0.5;
    // yPercent で要素サイズ相対に動かす(画像が wrapper の 200%、50% ずつオーバーフロー)
    const range = (1 - speedValue) * 50; // speed=0.6 → ±20% of element = ±40% of wrapper
    const trigger = target.closest('.js-parallax-view') || target;

    gsap.fromTo(
      target,
      { yPercent: -range },
      {
        yPercent: range,
        ease: 'none',
        scrollTrigger: {
          trigger,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      }
    );
  });
}

function initTestimonialSlider() {
  const sliders = document.querySelectorAll('.js-testimonial-slider');
  if (!sliders.length) return;

  // matchMedia は singleton (slider 数に関わらず 1 つのリスナーで全 slider を制御)
  const reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  const allSliderCallbacks = [];

  const onGlobalMqChange = () => {
    allSliderCallbacks.forEach(cb => cb());
  };
  if (typeof reducedMotionMq.addEventListener === 'function') {
    reducedMotionMq.addEventListener('change', onGlobalMqChange);
  } else if (typeof reducedMotionMq.addListener === 'function') {
    reducedMotionMq.addListener(onGlobalMqChange);
  }

  sliders.forEach(slider => {
    const track = slider.querySelector('.p-service-archive__testimonial-track');
    const items = slider.querySelectorAll(
      '.p-service-archive__testimonial-item'
    );
    const pagination = slider.parentElement.querySelector(
      '.js-testimonial-pagination'
    );
    if (!track || !items.length || !pagination) return;

    let activeIndex = 0;
    let autoplayId = null;

    const sliderLabel =
      slider.dataset.sliderLabel ||
      slider.getAttribute('aria-label') ||
      'お客様の声';
    // PHP の __() から data-dot-label-template で受け取る (SR フレンドリーな i18n 文)
    const dotLabelTpl =
      slider.dataset.dotLabelTemplate || `${sliderLabel} {index} / {total}`;

    // 単純な pagination として扱う(tablist 化は role=tabpanel 不足で破綻するため)
    const dots = Array.from(items).map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'p-service-archive__testimonial-pagination-dot';
      const label = dotLabelTpl
        .replace('{index}', String(i + 1))
        .replace('{total}', String(items.length));
      dot.setAttribute('aria-label', label);
      dot.addEventListener('click', () => goTo(i));
      pagination.appendChild(dot);
      return dot;
    });

    function update() {
      track.style.transform = `translateX(-${activeIndex * 100}%)`;
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === activeIndex);
        if (i === activeIndex) {
          d.setAttribute('aria-current', 'true');
        } else {
          d.removeAttribute('aria-current');
        }
      });
      // 非アクティブスライドを tab 順から除外
      // (translateX で画面外でもフォーカスは入る + Safari 旧版で inert 未対応のため aria-hidden 併用)
      items.forEach((item, i) => {
        const inactive = i !== activeIndex;
        item.toggleAttribute('inert', inactive);
        if (inactive) {
          item.setAttribute('aria-hidden', 'true');
        } else {
          item.removeAttribute('aria-hidden');
        }
      });
    }

    function goTo(i) {
      activeIndex = (i + items.length) % items.length;
      update();
      restartAutoplay();
    }

    function next() {
      goTo(activeIndex + 1);
    }

    function prev() {
      goTo(activeIndex - 1);
    }

    function stopAutoplay() {
      if (autoplayId) {
        clearInterval(autoplayId);
        autoplayId = null;
      }
    }

    function restartAutoplay() {
      // prefers-reduced-motion: reduce のユーザーには自動切替を提供しない
      if (reducedMotionMq.matches) return;
      // setInterval を一旦止めて再生成することで、ユーザー操作直後から 6 秒の
      // クールダウンを保証する(連続切替で interval が即座に走る事故を防ぐ)
      stopAutoplay();
      autoplayId = setInterval(next, 6000);
    }

    // singleton リスナーから呼ばれるコールバックを登録(slider 毎に分岐)
    allSliderCallbacks.push(() => {
      if (reducedMotionMq.matches) {
        stopAutoplay();
      } else {
        restartAutoplay();
      }
    });

    update();
    restartAutoplay();

    // ホバー / フォーカス中は自動切替を停止 (WCAG 2.2.2)
    slider.addEventListener('mouseenter', stopAutoplay);
    slider.addEventListener('mouseleave', restartAutoplay);
    slider.addEventListener('focusin', stopAutoplay);
    slider.addEventListener('focusout', restartAutoplay);

    // キーボード操作 (Left / Right / Home / End)
    // Arrow のみ preventDefault (Home/End は文書スクロールを止めるとユーザーを縛りすぎる)
    slider.addEventListener('keydown', e => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          next();
          break;
        case 'Home':
          goTo(0);
          break;
        case 'End':
          goTo(items.length - 1);
          break;
        default:
          break;
      }
    });
  });
}

/**
 * HERO 動画の autoplay 制御。
 *
 * HTML 側に autoplay 属性を入れず、reduced-motion を尊重して JS で動的に
 * 再生開始/停止を切り替える。matchMedia の change イベントにも追従するため、
 * OS 設定をその場で変えてもリロードなしに反映される。
 */
function setupHeroVideoAutoplay() {
  const videos = document.querySelectorAll('.p-service-archive__hero-video');
  if (!videos.length) return;
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');

  const apply = () => {
    videos.forEach(video => {
      if (mq.matches) {
        video.pause();
        // 黒枠が残らないよう CSS で非表示にする
        video.classList.add('is-reduced-motion-hidden');
      } else {
        video.classList.remove('is-reduced-motion-hidden');
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          // autoplay policy で拒否されても無視 (ユーザー操作待ち)
          playPromise.catch(() => {});
        }
      }
    });
  };

  apply();
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', apply);
  } else if (typeof mq.addListener === 'function') {
    // 旧 Safari 互換
    mq.addListener(apply);
  }
}

/**
 * Solution セクションのタブフィルター (ARIA tabs pattern, manual activation)。
 *
 * - 全カードを SSR 済 (.p-service-archive__solution-card-wrap[data-subcategory])
 * - タブクリック / キーボード操作 (←→/Home/End) で data-subcategory に一致する
 *   カードのみ表示する
 * - URL hash (#solution-technology 等) を読み書きしてリロード時の状態を保持
 * - aria-selected と tabindex を同期 (1 つだけ tab order に乗せる WAI-ARIA 標準)
 */
function initSolutionTabs() {
  const tablist = document.querySelector(
    '.p-service-archive__solution-tablist'
  );
  if (!tablist) return;
  const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
  const panel = document.querySelector('.p-service-archive__solution-list');
  if (!tabs.length || !panel) return;
  const cards = Array.from(
    panel.querySelectorAll('.p-service-archive__solution-card-wrap')
  );
  // aria-live region: タブ切替時に「N 件表示中」を SR にアナウンス
  // (単一 tabpanel + filter 方式は visual な表示変化を SR が拾いにくいため補助)
  const liveRegion = document.querySelector(
    '.p-service-archive__solution-live'
  );

  /**
   * 指定したタブをアクティブにする
   * @param {HTMLElement} targetTab
   * @param {{focus?: boolean, updateHash?: boolean, announce?: boolean}} [opts]
   */
  function activate(targetTab, opts = {}) {
    const { focus = true, updateHash = true, announce = true } = opts;
    const sub = targetTab.dataset.subcategory || 'all';

    tabs.forEach(tab => {
      const isActive = tab === targetTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    let visibleCount = 0;
    cards.forEach(card => {
      const cardSub = card.dataset.subcategory || '';
      const visible = sub === 'all' || cardSub === sub;
      card.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    // SR 向けアナウンス: タブ名 + 件数。タブ初期化時 (announce: false) は発話しない。
    if (announce && liveRegion) {
      const labelEl = targetTab.querySelector(
        '.p-service-archive__solution-tab-label'
      );
      const label =
        labelEl && labelEl.textContent ? labelEl.textContent.trim() : sub;
      liveRegion.textContent = `${label}: ${visibleCount} 件のサービスを表示中`;
    }

    if (focus) targetTab.focus();

    if (updateHash) {
      const hash = sub === 'all' ? '' : `#solution-${sub}`;
      const newUrl = hash || window.location.pathname + window.location.search;
      if (window.location.hash !== hash) {
        window.history.replaceState(null, '', newUrl);
      }
    }
  }

  // クリック (manual activation = タブクリックで切替、focus 移動だけでは切替えない)
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      activate(tab, { focus: true, updateHash: true });
    });
  });

  // キーボード: ← → Home End (WAI-ARIA Authoring Practices Tabs)
  tablist.addEventListener('keydown', e => {
    const currentIdx = tabs.indexOf(document.activeElement);
    if (currentIdx === -1) return;
    let nextIdx = -1;
    if (e.key === 'ArrowRight') {
      nextIdx = (currentIdx + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft') {
      nextIdx = (currentIdx - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      nextIdx = 0;
    } else if (e.key === 'End') {
      nextIdx = tabs.length - 1;
    }
    if (nextIdx !== -1) {
      e.preventDefault();
      activate(tabs[nextIdx], { focus: true, updateHash: true });
    }
  });

  // 初期表示: URL hash が #solution-<key> なら該当タブを開く
  const initialMatch = window.location.hash.match(/^#solution-([a-z0-9_-]+)$/i);
  let initialTab = tabs[0];
  if (initialMatch) {
    const key = initialMatch[1].toLowerCase();
    const matched = tabs.find(t => (t.dataset.subcategory || '') === key);
    if (matched) initialTab = matched;
  }
  activate(initialTab, { focus: false, updateHash: false, announce: false });
}

/**
 * 360° Intelligence Firm コンセプト図アニメ。
 *
 * - 楕円を順に描画 → ユニット出現
 * - ScrollTrigger でセクションが画面に入ったとき 1 回だけ再生
 * - prefers-reduced-motion: reduce は即座に最終状態を表示(動かさない)
 * - 座標は SVG 内の data 属性(viewBox 座標系)を使用
 */
function initConceptAnim() {
  const figure = document.querySelector('.js-concept-figure');
  if (!figure) return;
  const ellipses = figure.querySelectorAll('.js-concept-ellipse');
  const units = figure.querySelectorAll('.js-concept-unit');
  const badges = figure.querySelectorAll('.js-concept-badge-group');
  if (!units.length) return;

  const place = () => {
    units.forEach(unit => {
      gsap.set(unit.querySelector('.js-concept-badge-group'), {
        x: parseFloat(unit.dataset.bx),
        y: parseFloat(unit.dataset.by),
      });
      gsap.set(unit.querySelector('.js-concept-text-group'), {
        x: parseFloat(unit.dataset.tx),
        y: parseFloat(unit.dataset.ty),
      });
    });
  };

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    place();
    gsap.set(ellipses, {
      opacity: 1,
      strokeDasharray: 'none',
      strokeDashoffset: 0,
    });
    gsap.set(units, { opacity: 1 });
    gsap.set(badges, { opacity: 1 });
    return;
  }

  whenFontsReady(() => {
    place();
    gsap.set(ellipses, {
      opacity: 1,
      strokeDasharray: '1000 1000',
      strokeDashoffset: 1000,
    });
    gsap.set(units, { opacity: 0 });
    gsap.set(badges, { opacity: 0 });

    const tl = gsap.timeline({
      scrollTrigger: { trigger: figure, start: 'top 80%', once: true },
    });
    ellipses.forEach((ellipse, i) => {
      tl.to(
        ellipse,
        { strokeDashoffset: 0, duration: 1.0, ease: 'power2.inOut' },
        i === 0 ? 0 : '-=0.72'
      );
    });
    tl.to(
      units,
      { opacity: 1, duration: 0.55, ease: 'power3.out', stagger: 0.1 },
      '-=0.3'
    );
    tl.to(badges, { opacity: 1, duration: 0.4 }, '<');
  });
}

function initFaqAccordion() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('.faq-section .c-faq__item').forEach(item => {
    const trigger = item.querySelector('.c-faq__trigger');
    const answer = item.querySelector('.c-faq__answer');
    if (!trigger || !answer) return;

    if (item.hasAttribute('open')) {
      gsap.set(answer, { height: 'auto', overflow: '' });
    }

    trigger.addEventListener('click', e => {
      e.preventDefault();
      gsap.killTweensOf(answer);

      if (!item.hasAttribute('open')) {
        item.setAttribute('open', '');
        gsap.set(answer, { height: 0, overflow: 'hidden' });
        gsap.to(answer, {
          height: 'auto',
          duration: 0.4,
          ease: 'power3.out',
          onComplete: () => gsap.set(answer, { height: '', overflow: '' }),
        });
      } else {
        gsap.set(answer, { height: answer.offsetHeight, overflow: 'hidden' });
        gsap.to(answer, {
          height: 0,
          duration: 0.3,
          ease: 'power3.in',
          onComplete: () => {
            item.removeAttribute('open');
            gsap.set(answer, { height: '', overflow: '' });
          },
        });
      }
    });
  });
}

function initSpNav() {
  const nav = document.querySelector('.p-service-archive__sp-nav');
  if (!nav) return;

  const toggle = nav.querySelector('.p-service-archive__sp-nav-toggle');
  const drawer = nav.querySelector('.p-service-archive__sp-nav-drawer');
  if (!toggle || !drawer) return;

  let isOpen = false;

  const links = drawer.querySelectorAll('.p-service-archive__sp-nav-link');

  function openDrawer() {
    isOpen = true;
    toggle.setAttribute('aria-expanded', 'true');
    drawer.classList.add('is-open');
    gsap.killTweensOf(drawer);
    gsap.killTweensOf(links);
    gsap.set(drawer, { height: 0, overflow: 'hidden' });

    const tl = gsap.timeline({
      onComplete: () => gsap.set(drawer, { height: '', overflow: '' }),
    });
    tl.to(drawer, { height: 'auto', duration: 0.4, ease: 'power3.out' }).fromTo(
      links,
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.25, stagger: 0.06, ease: 'power2.out' },
      '-=0.25'
    );
  }

  function closeDrawer() {
    isOpen = false;
    toggle.setAttribute('aria-expanded', 'false');
    gsap.killTweensOf(drawer);
    gsap.killTweensOf(links);
    gsap.set(drawer, { height: drawer.offsetHeight, overflow: 'hidden' });

    const tl = gsap.timeline({
      onComplete: () => {
        drawer.classList.remove('is-open');
        gsap.set(drawer, { height: '', overflow: '' });
        gsap.set(links, { opacity: '', y: '' });
      },
    });
    tl.to(links, {
      opacity: 0,
      y: -6,
      duration: 0.15,
      stagger: { each: 0.04, from: 'end' },
      ease: 'power2.in',
    }).to(drawer, { height: 0, duration: 0.3, ease: 'power3.in' }, '-=0.05');
  }

  toggle.addEventListener('click', () => {
    if (isOpen) closeDrawer();
    else openDrawer();
  });

  nav.querySelectorAll('.p-service-archive__sp-nav-link').forEach(link => {
    link.addEventListener('click', () => {
      if (isOpen) closeDrawer();
    });
  });

  const solutionSection = document.querySelector(
    '.p-service-archive__solution-section'
  );
  if (solutionSection) {
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          nav.classList.add('is-hidden');
          if (isOpen) closeDrawer();
        } else if (entry.boundingClientRect.top > 0) {
          nav.classList.remove('is-hidden');
        }
      },
      { threshold: 0 }
    );
    observer.observe(solutionSection);
  }
}

function init() {
  setupHeroVideoAutoplay();
  initHeroAnim();
  whenFontsReady(() => initRevealLines());
  initTextInvert();
  initWordAnim();
  initRrTitleAnim();
  initFadeAnim();
  initParallax();
  initTestimonialSlider();
  initSolutionTabs();
  initConceptAnim();
  initFaqAccordion();
  initSpNav();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// 画像読み込み完了後にトリガー位置を再計算 (parallax 等が画像サイズに依存するため)
window.addEventListener('load', () => {
  ScrollTrigger.refresh();

  // ロゴマーキー: 画像がすべて読まれてからアニメーション開始
  // (トラック幅が画像の自然幅の合計で決まるため、読み込み前に動かすと幅0のまま滑る)
  const track = document.querySelector('.p-service-archive__clients-track');
  if (track) {
    const imgs = Array.from(track.querySelectorAll('img'));
    const total = imgs.length;

    if (total === 0) {
      track.classList.add('is-ready');
    } else {
      const start = () => {
        window.clearTimeout(fallbackId);
        track.classList.add('is-ready');
      };

      /*
       * 全枚数の読み込みを待つが、待ち続けて止まったままにはしない。
       *
       * トラックは 2 セット分あり overflow: hidden で大半が常に可視範囲の外に
       * あるため、img が loading="lazy" だとビューポートと交差せず永久に
       * 読み込まれない。実際それで帯が停止していた。テンプレート側は eager を
       * 出しているが、そこだけに依存すると lazy に戻された瞬間また静かに止まる
       * (エラーも lint も出ないので気づけない)。上流の指定を二重化する代わりに、
       * 読み込みが揃わなくても既定時間で動き出す形にして停止そのものを無くす。
       */
      const fallbackId = window.setTimeout(start, 3000);

      let loaded = 0;
      const onReady = () => {
        loaded++;
        if (loaded >= total) start();
      };

      imgs.forEach(img => {
        if (img.complete) {
          onReady();
          return;
        }
        img.addEventListener('load', onReady, { once: true });
        img.addEventListener('error', onReady, { once: true });
      });
    }
  }
});
